import { ErrorKey } from '@/models/enums/app/ErrorKey'
import { SESSION_RENEWED_EVENT } from '@/constants/authEvents'
import { RoutesKey } from '@/models/enums/app/RoutesKey'
import { StoresList } from '@/models/enums/app/StoresList'
import { NotificationType } from '@/models/enums/app/NotificationType'
import { UserRoles } from '@/models/enums/auth/UserRoles'
import { ProfilPortee } from '@/models/interfaces/auth/Profil'
import type {
  EmailVerifierValues,
  SignInValues,
  SignUpValues,
} from '@/models/interfaces/auth/AuthenticationsValues'
import type { User, UserSubmission } from '@/models/interfaces/auth/User'
import type { FileObject } from '@/models/interfaces/object/FileObject'
import { i18n } from '@/plugins/i18n'
import { addNotification } from '@/services/notifications/NotificationService'
import FileUploader from '@/services/files/FileUploader'
import { AuthenticationService } from '@/services/userAndAuth/AuthenticationService'
import JwtCookie from '@/services/userAndAuth/JWTCookie'
import { UserService } from '@/services/userAndAuth/UserService'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import {
  GEOPORTAIL_COMMUNE_MODE_QUERY,
  GEOPORTAIL_MODE_QUERY_KEY,
} from '@/services/commune/navigateGeoportailCommune'
import { computed, ref } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'

export const useUserStore = defineStore(StoresList.USER, () => {
  const router = useRouter()
  const route = useRoute()
  const loginCheck = ref(false)
  const currentUser = ref<User | null>(null)
  const errorWhileSignIn = ref(false)
  const errorWhileSignUp = ref('')
  const errorWhileUpdatingUser = ref(false)
  const invalidAccount = ref(false)
  const sessionExpirationTimer = ref<number | null>(null)

  const userIsLogged = computed(() => currentUser.value !== null)

  const userHasRole = (role: UserRoles) =>
    userIsLogged.value && currentUser.value?.roles.includes(role)

  const userIsSuperAdmin = () => userIsLogged.value && userHasRole(UserRoles.SUPER_ADMIN)

  /**
   * Accès à l'espace d'administration (habilitation ACCES_ADMINISTRATION) : Super Administrateur et
   * Administrateur Fonctionnel par défaut, accordable à d'autres profils depuis la page « Gestion
   * des habilitations ».
   */
  const userIsAdmin = () => !!(userIsLogged.value && userHasRole(UserRoles.ACCES_ADMINISTRATION))

  const userIsEditor = () => {
    return userIsLogged.value && (userIsAdmin() || userHasRole(UserRoles.AGENT_COMMUNE))
  }

  /** Validation et publication des contenus (projets, ressources, acteurs) d'autrui. */
  const userModereContenu = () =>
    !!(userIsLogged.value && userHasRole(UserRoles.MODERATION_CONTENU))

  /** Paramétrage du référentiel : types d'infrastructure, attributs et choix, styles, icônes. */
  const userPeutParametrer = () =>
    !!(userIsLogged.value && userHasRole(UserRoles.GESTION_PARAMETRAGE))
  /** Indicateurs : création, méthode de calcul, recalcul, activation, suppression. */
  const userPeutGererIndicateurs = () =>
    !!(userIsLogged.value && userHasRole(UserRoles.GESTION_INDICATEURS))
  /** Volet thématique du géoportail : domaines, sous-domaines, compétences et leurs liaisons. */
  const userPeutGererThematiques = () =>
    !!(userIsLogged.value && userHasRole(UserRoles.GESTION_THEMATIQUES))
  /** Une des trois habilitations du menu « Paramétrage & Indicateur ». */
  const userAccedeAuParametrage = () =>
    userPeutParametrer() || userPeutGererIndicateurs() || userPeutGererThematiques()

  /** Gestion des comptes, une habilitation par action. */
  const userPeutListerUtilisateurs = () => !!userHasRole(UserRoles.READ_UTILISATEUR)
  const userPeutCreerUtilisateurs = () => !!userHasRole(UserRoles.CREATE_UTILISATEUR)
  const userPeutModifierUtilisateurs = () => !!userHasRole(UserRoles.WRITE_UTILISATEUR)
  const userPeutSupprimerUtilisateurs = () => !!userHasRole(UserRoles.DELETE_UTILISATEUR)
  const userPeutGererHabilitations = () => !!userHasRole(UserRoles.GESTION_HABILITATIONS)
  /** Synchronisation avec la plateforme en ligne : lancement, heure automatique, historique. */
  const userPeutGererSynchronisation = () => !!userHasRole(UserRoles.GESTION_SYNCHRONISATION)

  /**
   * Accès à l'espace d'administration : ACCES_ADMINISTRATION, ou la seule synchronisation (maire et
   * administrateur technique d'une instance communale, qui n'y voient que ce panneau).
   */
  const userAccedeAdministration = () => userIsAdmin() || userPeutGererSynchronisation()

  /**
   * Tableau de bord communal (habilitation ACCES_DASHBOARD_COMMUNAL, l'Administrateur Commune par
   * défaut) effectivement ouvrable : la commune du profil fixe le territoire affiché.
   */
  const userGouverneUneCommune = computed(
    () =>
      !!userHasRole(UserRoles.ACCES_DASHBOARD_COMMUNAL) && !!currentUser.value?.commune?.cmnCode,
  )

  /**
   * Vue nationale du tableau de bord (habilitation ACCES_DASHBOARD_CENTRAL). Seule source de la
   * règle, pour les menus comme pour la garde de route, qui ne peuvent donc plus diverger.
   */
  const userPeutConsulterDashboardCentral = computed(
    () => !!userHasRole(UserRoles.ACCES_DASHBOARD_CENTRAL),
  )

  /** Profil communal : sa portée se limite au portail de la commune à laquelle il est rattaché. */
  const userEstCommunal = computed(
    () => userIsLogged.value && currentUser.value?.profil?.profilPortee === ProfilPortee.COMMUNAL,
  )

  /**
   * Espace d'arrivée d'un profil communal, qui n'a pas l'usage de l'accueil national : le tableau de
   * bord pour l'exécutif, le géoportail de sa commune pour les autres rôles locaux. Nul pour un
   * compte central, ou pour un compte local qu'aucune commune ne rattache encore.
   */
  const espaceCommunal = (): RouteLocationRaw | null => {
    if (!userEstCommunal.value) return null
    if (userGouverneUneCommune.value) return { name: RoutesKey.DASHBOARD_MAIRE }

    const nomCommune = currentUser.value?.commune?.cmnTitre
    if (!nomCommune) return null
    return {
      name: RoutesKey.GEOPORTAIL,
      query: { commune: nomCommune, [GEOPORTAIL_MODE_QUERY_KEY]: GEOPORTAIL_COMMUNE_MODE_QUERY },
    }
  }

  /** Page d'arrivée après connexion : l'espace de sa commune pour un profil communal. */
  const routeApresConnexion = (): RouteLocationRaw => espaceCommunal() ?? { name: RoutesKey.HOME }

  const signIn = async (values: SignInValues, hideDialog = true) => {
    try {
      const response = await AuthenticationService.signIn(values)
      const { token } = response.data

      if (!values.stayLoggedIn) {
        await JwtCookie.deleteRefreshToken()
      }
      JwtCookie.setTokenInCookie(token)
      await setCurrentUser()
      scheduleSessionExpirationLogout()
      notifySessionRenewed()
      errorWhileSignIn.value = false
      if (hideDialog) {
        // Un redirect explicite (session expirée, page protégée) garde la priorité.
        const redirectTo = (route.query.redirect as string) || routeApresConnexion()
        await router.push(redirectTo)
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        invalidAccount.value = true
        errorWhileSignIn.value = true
        return
      }
      errorWhileSignIn.value = true
    }
  }

  const setCurrentUser = async () => {
    await getAuthenticatedUser()
  }

  const getAuthenticatedUser = async () => {
    currentUser.value = (await AuthenticationService.getAuthenticatedUser()).data
  }

  const signUp = async (values: SignUpValues) => {
    errorWhileSignUp.value = ''
    await UserService.createUser(values).catch((error) => {
      if (error.response && error.response.status === 422) {
        const data = error.response.data

        const violations = data.violations || []

        const isEmailTaken = violations.some(
          (v: { propertyPath: string; message: string }) =>
            v.propertyPath === 'userEmail' && v.message === 'Cette valeur est déjà utilisée.',
        )
        if (isEmailTaken) {
          errorWhileSignUp.value = ErrorKey.EMAILTAKEN
        } else {
          errorWhileSignUp.value = ErrorKey.BADREQUEST
        }
      } else {
        // Erreur serveur ou réseau : le compte n'a pas été créé, l'écran ne doit pas annoncer
        // l'envoi d'un courriel de vérification.
        errorWhileSignUp.value = ErrorKey.BADREQUEST
      }
    })
  }

  const signOut = async () => {
    clearSessionExpirationLogout()
    currentUser.value = null
    JwtCookie.clearCookies()
    router.replace({ name: RoutesKey.HOME })
    location.reload()
  }

  const clearSessionExpirationLogout = () => {
    if (sessionExpirationTimer.value !== null) {
      window.clearTimeout(sessionExpirationTimer.value)
      sessionExpirationTimer.value = null
    }
  }

  const notifySessionRenewed = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_RENEWED_EVENT))
    }
  }

  const handleSessionExpired = async (redirectPath?: string) => {
    clearSessionExpirationLogout()
    currentUser.value = null
    JwtCookie.clearCookies()

    addNotification(i18n.t('auth.sessionExpiredReconnect'), NotificationType.ERROR)

    const currentPath = router.currentRoute.value.fullPath
    const redirect = redirectPath || currentPath

    if (router.currentRoute.value.name !== RoutesKey.AUTH_SIGN_IN) {
      await router.push({
        name: RoutesKey.AUTH_SIGN_IN,
        query: {
          redirect,
        },
      })
    }
  }

  const scheduleSessionExpirationLogout = () => {
    clearSessionExpirationLogout()

    const expiresAt = JwtCookie.getTokenExpirationTimestampMs(JwtCookie.getToken())
    if (!expiresAt) {
      return
    }

    const timeoutMs = expiresAt - Date.now()
    if (timeoutMs <= 0) {
      void handleSessionExpired()
      return
    }

    sessionExpirationTimer.value = window.setTimeout(() => {
      void handleSessionExpired()
    }, timeoutMs)
  }

  const verifyEmail = async (emailVerifierValues: EmailVerifierValues) => {
    await AuthenticationService.verifyEmail(emailVerifierValues)
    if (userIsLogged.value) {
      await getAuthenticatedUser()
    }
  }

  const checkAuthenticated = async () => {
    const jwtCookieIsValid = await JwtCookie.isValid()
    if (jwtCookieIsValid) {
      await setCurrentUser()
      scheduleSessionExpirationLogout()
      notifySessionRenewed()
    } else {
      clearSessionExpirationLogout()
    }
    loginCheck.value = true
  }

  const patchUser = async (
    values: Partial<UserSubmission>,
    updateLogo = false,
    logo: File | null = null,
  ) => {
    try {
      errorWhileUpdatingUser.value = false
      if (values.logo && (values.logo as FileObject)['@id']) {
        values.logo = (values.logo as FileObject)['@id']
      }
      if (updateLogo && logo) {
        const newLogo = await FileUploader.uploadFile(logo)
        values.logo = newLogo['@id']
      }
      await UserService.patchUser(values, currentUser.value!.id)
      setCurrentUser()
      router.replace({ query: { ...route.query, dialog: undefined } })
    } catch (error) {
      console.log(error)
      errorWhileUpdatingUser.value = true
    }
  }

  return {
    loginCheck,
    userIsLogged,
    userIsSuperAdmin,
    userIsAdmin,
    userIsEditor,
    userModereContenu,
    userPeutParametrer,
    userPeutGererIndicateurs,
    userPeutGererThematiques,
    userAccedeAuParametrage,
    userPeutListerUtilisateurs,
    userPeutCreerUtilisateurs,
    userPeutModifierUtilisateurs,
    userPeutSupprimerUtilisateurs,
    userPeutGererHabilitations,
    userPeutGererSynchronisation,
    userAccedeAdministration,
    userGouverneUneCommune,
    userPeutConsulterDashboardCentral,
    userEstCommunal,
    espaceCommunal,
    userHasRole,
    currentUser,
    errorWhileSignUp,
    errorWhileSignIn,
    errorWhileUpdatingUser,
    invalidAccount,
    signIn,
    signUp,
    signOut,
    verifyEmail,
    checkAuthenticated,
    patchUser,
    handleSessionExpired,
    scheduleSessionExpirationLogout,
  }
})
